import type { RouteParams } from 'vue-router';
import type { StoredFilter } from '@/types/filter.types';
import type { ApiInfo, Tenant } from '@/types/api.types';

import router from '@/router';
import { dashboard } from '@/helpers/contants';
import { defineStore } from 'pinia';
import { defaultSiteKey } from '@/composables/useRecaptcha';
import { getShadedColor } from '@/helpers/utility.helper';
import { createBulmaCssVars } from '@/helpers/create-bulma-css-vars';

export const useApplicationStore = defineStore(
    'applicationStore',
    () => {
        const calendarWidgetUrl = ref(dashboard.CALENDAR_WIDGET_URL);
        const dashboardView = ref(dashboard.LIST_TYPE_DEFAULT);
        const deepLink = ref<{ name: string; params: RouteParams }>();
        const organizationsWithFirstTimeAlert = ref<any[]>();
        const oAuthLink = ref<string>();
        const urlFilters = ref<Record<string, StoredFilter>>({});
        const info = ref<ApiInfo>();
        const isMenuCollapsed = ref(false);
        const coupons = ref<string[]>([]);

        const selectedEntityId = ref<string>();

        const captchaKey = computed(() => {
            const mode = import.meta.env.MODE;

            return ['e2e'].includes(mode) ? defaultSiteKey : info.value?.captcha?.site_key;
        });

        const hasOrganizationFirstTimeAlert = computed(() => (id) => organizationsWithFirstTimeAlert.value?.find((s) => s === id));

        const hasQueryParamsBasedUrl = computed(() => (name) => urlFilters.value[name]);

        const testMode = computed(() => info.value?.test_mode);

        const tenant = computed<Tenant>(() => info.value?.tenant ?? ({} as Tenant));

        const tenantInfo = computed(() => {
            return {
                name: tenant.value?.name ?? 'Firm24',
                contactPhone: tenant.value?.contactPhone,
                contactEmail: tenant.value?.contactEmail,
            };
        });

        const isDefaultTenant = computed(() => tenant.value?.alias === 'firm24');

        function toggleMenu() {
            isMenuCollapsed.value = !isMenuCollapsed.value;

            localStorage.setItem('isMenuCollapsed', isMenuCollapsed.value?.toString());
        }

        function updateDashboardView(v) {
            dashboardView.value = v;
        }

        function updateOrganizationsWithFirstTimeAlert(v) {
            organizationsWithFirstTimeAlert.value?.push(v);
        }

        function updateDeepLink(v) {
            deepLink.value = v;
        }

        function updateOAuthLink(v) {
            oAuthLink.value = v;
        }

        function updateUrlFilters(v) {
            urlFilters.value[router.currentRoute.value.name as string] = v;
        }

        function updateInfo(v) {
            info.value = v;

            if (info.value?.tenant) {
                if (!info.value.tenant.headerColor) {
                    info.value.tenant.headerColor = info.value.tenant.primaryColor;
                }
                const { primaryColor = '', headerColor = '' } = info.value.tenant;
                createBulmaCssVars({ primaryColor });

                document.documentElement.style.setProperty('--tenancy-primary-color', primaryColor);
                document.documentElement.style.setProperty('--tenancy-header-bg-color', headerColor);
                document.documentElement.style.setProperty('--tenancy-header-bg-contrast-color', getShadedColor(headerColor));
                document.documentElement.style.setProperty('--tenancy-primary-contrast-color', getShadedColor(primaryColor));
            }
        }

        return {
            calendarWidgetUrl,
            dashboardView,
            deepLink,
            organizationsWithFirstTimeAlert,
            oAuthLink,
            urlFilters,
            info,
            isMenuCollapsed,
            captchaKey,
            hasOrganizationFirstTimeAlert,
            hasQueryParamsBasedUrl,
            testMode,
            tenant,
            tenantInfo,
            coupons,
            isDefaultTenant,
            toggleMenu,
            updateDashboardView,
            updateOrganizationsWithFirstTimeAlert,
            updateDeepLink,
            updateOAuthLink,
            updateUrlFilters,
            updateInfo,
            selectedEntityId,
        };
    },
    {
        persistedState: {
            includePaths: ['isMenuCollapsed', 'coupons', 'selectedEntityId'],
        },
    },
);
